import React, { Fragment } from 'react';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import { I18n, Translate } from 'react-redux-i18n';

import AnalyticsServer from '../../../app/server/analytics';
import ROUTES from '../../../data/routes';
import {
  defaultAnalyticsDateRange,
  formatAnalyticsApiDate,
} from '../utils/dateRange';
import {
  AnalyticsKpiGrid,
  formatKpiValue,
} from './AnalyticsVisuals';

const TZ = 'America/Santiago';

const OVERVIEW_KPIS_COMMERCE = [
  { key: 'activeUsers', label: 'analyticsActiveUsers', format: 'number' },
  { key: 'sessions', label: 'analyticsSessions', format: 'number' },
  { key: 'pageViews', label: 'analyticsPageViews', format: 'number' },
  { key: 'viewItem', label: 'analyticsViewItem', format: 'number' },
  { key: 'addToCart', label: 'analyticsAddToCart', format: 'number' },
  { key: 'beginCheckout', label: 'analyticsBeginCheckout', format: 'number' },
  { key: 'ecommercePurchases', label: 'analyticsPurchases', format: 'number' },
  { key: 'purchaseRevenue', label: 'analyticsPurchaseRevenue', format: 'revenue' },
];

const OVERVIEW_KPIS_COMMERCIAL = [
  { key: 'solicitudNuevosUsuarios', label: 'analyticsSolicitudNuevosUsuarios', format: 'number' },
  { key: 'cantidadCotizaciones', label: 'analyticsCantidadCotizaciones', format: 'number' },
  { key: 'montoCotizaciones', label: 'analyticsMontoCotizaciones', format: 'revenue' },
];

const OVERVIEW_KPIS_ENGAGEMENT = [
  { key: 'generateEstimate', label: 'analyticsGenerateEstimate', format: 'number' },
  { key: 'registrationRequest', label: 'analyticsRegistrationRequest', format: 'number' },
  { key: 'login', label: 'analyticsLogin', format: 'number' },
  { key: 'signUp', label: 'analyticsSignUp', format: 'number' },
];

function defaultDateRange() {
  return defaultAnalyticsDateRange(6);
}

function formatApiDate(date) {
  return formatAnalyticsApiDate(date);
}

class HomeResumo extends React.PureComponent {
  constructor(props) {
    super(props);
    const { startDate, endDate } = defaultDateRange();
    this.state = {
      startDate,
      endDate,
      loading: false,
      overview: null,
      commercialSummary: null,
      lakeStatus: null,
      errorMessage: '',
    };
  }

  componentDidMount() {
    this.loadOverview();
  }

  loadOverview = async () => {
    const { startDate, endDate } = this.state;
    const start = formatApiDate(startDate);
    const end = formatApiDate(endDate);

    this.setState({ loading: true, errorMessage: '' });

    try {
      const [overview, commercialSummary, lakeStatus] = await Promise.all([
        AnalyticsServer.getHistoryOverview(start, end),
        AnalyticsServer.getCommercialSummary(start, end),
        AnalyticsServer.getLakeStatus(),
      ]);

      this.setState({
        overview,
        commercialSummary,
        lakeStatus,
        loading: false,
      });
    } catch (err) {
      const error = err.response && err.response.data && err.response.data.error;
      this.setState({
        loading: false,
        errorMessage: (error && error.message) || I18n.t('unknownError'),
      });
    }
  };

  renderLakeBadge = () => {
    const { lakeStatus, overview } = this.state;
    const badges = [];

    if (overview && overview.source === 'ga4_direct') {
      badges.push(
        <span key="ga4" className="badge badge-info">
          <Translate value="analyticsSourceDirect" />
        </span>,
      );
    }

    if (!lakeStatus) {
      return badges.length ? <Fragment>{badges}</Fragment> : null;
    }

    if (!lakeStatus.enabled || !lakeStatus.configured) {
      badges.push(
        <span key="lake" className="badge badge-secondary">
          <Translate value="analyticsLakeOff" />
        </span>,
      );
    }

    return badges.length ? <Fragment>{badges}</Fragment> : null;
  };

  renderKpiSection = (titleKey, kpis, totals) => (
    <div className="analytics-kpi-section">
      <h5 className="analytics-kpi-section__title">
        <Translate value={titleKey} />
      </h5>
      <AnalyticsKpiGrid
        items={kpis.map(({ key, label, format }) => ({
          key,
          labelKey: label,
          value: formatKpiValue(totals[key], format),
        }))}
      />
    </div>
  );

  renderOverviewKpis = (totals) => (
    <Fragment>
      {this.renderKpiSection('analyticsKpiCommerce', OVERVIEW_KPIS_COMMERCE, totals)}
      {this.renderKpiSection('analyticsKpiEngagement', OVERVIEW_KPIS_ENGAGEMENT, totals)}
    </Fragment>
  );

  renderEmpty = () => (
    <div className="analytics-empty">
      <p>
        <Translate value="analyticsEmpty" />
      </p>
      <p>
        <Translate value="analyticsSeeLiveForNow" />
      </p>
    </div>
  );

  render() {
    const { loading, overview, commercialSummary, errorMessage } = this.state;
    const isEmpty = overview && overview.empty;
    const extractedAt = overview && overview.extractedAt;

    return (
      <div className="content analytics-page">
        <h1 className="page-header">
          <Translate value="analyticsHomeTitle" />
        </h1>

        {loading && !overview && (
          <p className="margin-top-20">
            <Translate value="analyticsLoading" />
          </p>
        )}

        {errorMessage && (
          <p className="color-red margin-top-20">{errorMessage}</p>
        )}

        {isEmpty && !commercialSummary && (
          <Fragment>
            {this.renderEmpty()}
            {this.state.lakeStatus && (
              <div className="analytics-meta">
                {this.renderLakeBadge()}
              </div>
            )}
          </Fragment>
        )}

        {commercialSummary && (
          <Fragment>
            {this.renderKpiSection(
              'analyticsKpiCommercial',
              OVERVIEW_KPIS_COMMERCIAL,
              commercialSummary,
            )}
          </Fragment>
        )}

        {!isEmpty && overview && overview.totals && (
          <Fragment>
            {extractedAt && (
              <div className="analytics-meta">
                <span>
                  {I18n.t('analyticsUpdatedAt')}
                  {': '}
                  {moment(extractedAt).tz(TZ).format('L LT')}
                </span>
                {this.renderLakeBadge()}
              </div>
            )}
            {this.renderOverviewKpis(overview.totals)}
          </Fragment>
        )}

        <p className="margin-top-20">
          <Link to={ROUTES.ANALYTICS} className="btn btn-primary">
            <Translate value="analyticsHomeCta" />
          </Link>
        </p>
      </div>
    );
  }
}

export default HomeResumo;
